import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('namaa_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('namaa_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Regions
export const getRegions = () => api.get('/regions');
export const getRegion = (id) => api.get(`/regions/${id}`);
export const createRegion = (data) => api.post('/regions', data);
export const updateRegion = (id, data) => api.put(`/regions/${id}`, data);
export const deleteRegion = (id) => api.delete(`/regions/${id}`);

// Cities
export const getCities = (regionId) => api.get('/cities', { params: { region_id: regionId } });
export const getCity = (id) => api.get(`/cities/${id}`);
export const createCity = (data) => api.post('/cities', data);
export const updateCity = (id, data) => api.put(`/cities/${id}`, data);
export const deleteCity = (id) => api.delete(`/cities/${id}`);

// Districts
export const getDistricts = (cityId) => api.get('/districts', { params: { city_id: cityId } });
export const getDistrict = (id) => api.get(`/districts/${id}`);
export const createDistrict = (data) => api.post('/districts', data);
export const updateDistrict = (id, data) => api.put(`/districts/${id}`, data);
export const deleteDistrict = (id) => api.delete(`/districts/${id}`);

// Categories
export const getCategories = () => api.get('/categories');
export const getCategory = (id) => api.get(`/categories/${id}`);
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// SubCategories
export const getSubCategories = (categoryId) => api.get('/subcategories', { params: { category_id: categoryId } });
export const getSubCategory = (id) => api.get(`/subcategories/${id}`);
export const createSubCategory = (data) => api.post('/subcategories', data);
export const updateSubCategory = (id, data) => api.put(`/subcategories/${id}`, data);
export const deleteSubCategory = (id) => api.delete(`/subcategories/${id}`);

// Map Display Categories
export const getMapDisplayCategories = () => api.get('/map-display-categories');
export const createMapDisplayCategory = (data) => api.post('/map-display-categories', data);
export const updateMapDisplayCategory = (id, data) => api.put(`/map-display-categories/${id}`, data);
export const deleteMapDisplayCategory = (id) => api.delete(`/map-display-categories/${id}`);

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const deleteAllProjects = () => api.delete('/projects/bulk/all');
export const deleteAllRegionStatistics = () => api.delete('/region-statistics/bulk/all');
export const fixProjectCategories = () => api.post('/projects/fix-categories');
export const seedDemoData = () => api.post('/seed-demo-data');

// Sales Outlets
export const getOutlets = (params) => api.get('/outlets', { params });
export const getOutlet = (id) => api.get(`/outlets/${id}`);
export const createOutlet = (data) => api.post('/outlets', data);
export const updateOutlet = (id, data) => api.put(`/outlets/${id}`, data);
export const deleteOutlet = (id) => api.delete(`/outlets/${id}`);

// Statistics
export const getStatisticDefinitions = () => api.get('/statistics/definitions');
export const createStatisticDefinition = (data) => api.post('/statistics/definitions', data);
export const updateStatisticDefinition = (id, data) => api.put(`/statistics/definitions/${id}`, data);
export const deleteStatisticDefinition = (id) => api.delete(`/statistics/definitions/${id}`);

export const getStatisticValues = (params) => api.get('/statistics/values', { params });
export const createStatisticValue = (data) => api.post('/statistics/values', data);
export const updateStatisticValue = (id, data) => api.put(`/statistics/values/${id}`, data);
export const deleteStatisticValue = (id) => api.delete(`/statistics/values/${id}`);

// Aliases
export const getAliases = (entityType) => api.get('/aliases', { params: { entity_type: entityType } });
export const createAlias = (data) => api.post('/aliases', data);
export const deleteAlias = (id) => api.delete(`/aliases/${id}`);

// Review Queue
export const getReviewQueue = (params) => api.get('/review-queue', { params });
export const updateReviewItem = (id, data) => api.put(`/review-queue/${id}`, data);

// Imports
export const getImports = () => api.get('/imports');
export const getImport = (id) => api.get(`/imports/${id}`);
export const getImportErrors = (id) => api.get(`/imports/${id}/errors`);
export const uploadImportFile = (file, importType) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('import_type', importType);
  return api.post('/imports/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const processImport = (id) => api.post(`/imports/${id}/process`);
export const previewImport = (id) => api.post(`/imports/${id}/preview`);

// Media
export const getMediaFiles = (params) => api.get('/media', { params });
export const uploadMedia = (file, entityType, entityId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (entityType) formData.append('entity_type', entityType);
  if (entityId) formData.append('entity_id', entityId);
  return api.post('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteMedia = (id) => api.delete(`/media/${id}`);

// Region Statistics (بنك التنمية الاجتماعية)
export const getRegionStatistics = () => api.get('/region-statistics');
export const getRegionStatistic = (regionId) => api.get(`/region-statistics/${regionId}`);
export const upsertRegionStatistic = (data) => api.post('/region-statistics', data);
export const deleteRegionStatistic = (regionId) => api.delete(`/region-statistics/${regionId}`);

// Image Upload & AI
export const uploadProjectImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getImageUrl = (path) => `${api.defaults.baseURL}/images/${path}`;
export const getFileUrl = (path) => `${api.defaults.baseURL}/files/${path}`;
export const aiImproveDescription = (text) => api.post('/ai/improve-description', { text });
export const aiGenerateShortDesc = (name, text) => api.post('/ai/generate-short-description', { name, text });
export const aiGenerateTags = (name, category, text) => api.post('/ai/generate-tags', { name, category, text });
export const extractCoordinates = (url) => api.post('/extract-coordinates', { url });

export const uploadPdf = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload-pdf', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Full project details (public)
export const getProjectFull = (id) => api.get(`/projects/${id}/full`);

// Success Stories
export const getSuccessStories = (params) => api.get('/success-stories', { params });
export const getSuccessStory = (id) => api.get(`/success-stories/${id}`);
export const getSuccessStoryFull = (id) => api.get(`/success-stories/${id}/full`);
export const getSuccessStoriesMapData = () => api.get('/success-stories/map-data');
export const createSuccessStory = (data) => api.post('/success-stories', data);
export const updateSuccessStory = (id, data) => api.put(`/success-stories/${id}`, data);
export const deleteSuccessStory = (id) => api.delete(`/success-stories/${id}`);
export const deleteAllSuccessStories = () => api.delete('/success-stories/bulk/all');
export const seedDemoStories = () => api.post('/seed-demo-stories');

// Success Stories AI
export const aiStoryHook = (data) => api.post('/ai/story-hook', data);
export const aiStoryImproveChallenge = (data) => api.post('/ai/story-improve-challenge', data);
export const aiStoryResultsSummary = (data) => api.post('/ai/story-results-summary', data);
export const aiStorySupportTags = (data) => api.post('/ai/story-support-tags', data);
export const aiGenerateStoryFromProject = (data) => api.post('/ai/generate-story-from-project', data);

// Project -> Story conversion
export const convertProjectToStory = (projectId) => api.post(`/projects/${projectId}/convert-to-story`);
export const checkProjectHasStory = (projectId) => api.get(`/projects/${projectId}/has-story`);

// Project search (lightweight autocomplete)
export const searchProjectsLight = (q) => api.get('/projects/search-light', { params: { q } });

// Project Products
export const getProjectProducts = (projectId) => api.get(`/projects/${projectId}/products`);
export const createProjectProduct = (projectId, data) => api.post(`/projects/${projectId}/products`, data);
export const updateProjectProduct = (projectId, productId, data) => api.put(`/projects/${projectId}/products/${productId}`, data);
export const deleteProjectProduct = (projectId, productId) => api.delete(`/projects/${projectId}/products/${productId}`);

// Project Sales Channels
export const getProjectSalesChannels = (projectId) => api.get(`/projects/${projectId}/sales-channels`);
export const createProjectSalesChannel = (projectId, data) => api.post(`/projects/${projectId}/sales-channels`, data);
export const updateProjectSalesChannel = (projectId, channelId, data) => api.put(`/projects/${projectId}/sales-channels/${channelId}`, data);
export const deleteProjectSalesChannel = (projectId, channelId) => api.delete(`/projects/${projectId}/sales-channels/${channelId}`);

// Project Product Images
export const getProjectProductImages = (projectId) => api.get(`/projects/${projectId}/product-images`);
export const createProjectProductImage = (projectId, data) => api.post(`/projects/${projectId}/product-images`, data);
export const deleteProjectProductImage = (projectId, imageId) => api.delete(`/projects/${projectId}/product-images/${imageId}`);

export default api;
